import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useGetAllScansQuery } from '../../../services/entities/impl/blfApi/endpoints/scans';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import { Customer } from '../../../services/entities/impl/blfApi/schemas/customer';
import { Ranch } from '../../../services/entities/impl/blfApi/schemas/ranch';
import { useFeatureFlags } from '../../../services/featureFlags/hooks/useFeatureFlags';
import {
  selectCurrBlockID,
  selectCurrCustomerID,
  selectCurrRanchID,
  setCurrRanchByID,
} from '../../select/selectSlice';

type Props = {
  customers?: Customer[];
  ranches?: Ranch[];
  blocks?: Block[];
};

export const useGetDropdownFilteredData = ({
  customers,
  ranches,
  blocks,
}: Props) => {
  const dispatch = useAppDispatch();
  const { Diagnostics } = useFeatureFlags();
  const currCustomerID = useAppSelector(selectCurrCustomerID);
  const currRanchID = useAppSelector(selectCurrRanchID);
  const currBlockID = useAppSelector(selectCurrBlockID);

  // Inactive customers are already filtered on backend side
  const customersFiltered = customers;

  // Make a list of the active customer IDs
  const activeCustomerIDs = useMemo(() => {
    return customersFiltered?.map((customer) => customer.id);
  }, [customersFiltered]);

  // TODO: check if we have ranches with deactivated customers
  // because for now we added soft deleting related childs in the backend
  // and this mean that we don't have ranches with deactivated customers
  const ranchesFiltered = useMemo(
    () =>
      ranches?.filter(
        (ranch) =>
          ranch.customerID === currCustomerID &&
          activeCustomerIDs?.includes(ranch.customerID)
      ),
    [ranches, currCustomerID, activeCustomerIDs]
  );

  // Make a list of active ranch IDs (using active customers list)
  const activeRanchIDs = useMemo(() => {
    return ranchesFiltered?.map((ranch) => ranch.id);
  }, [ranchesFiltered]);

  // Filter out blocks that don't have a geoPolygon and are not in the active ranches list
  // Diagnostics has no filtering to show new blocks to add geoJSONs
  const blocksActive = useMemo(() => {
    return Diagnostics
      ? blocks
      : blocks?.filter(
          (block) =>
            block.geoPolygon !== null && activeRanchIDs?.includes(block.ranchID)
        );
  }, [blocks, activeRanchIDs, Diagnostics]);

  const blocksFiltered = useMemo(() => {
    // Show all blocks if no selections are made
    let blocksFiltered = blocksActive;
    if (currBlockID !== '') {
      // Show only the selected block if one is selected
      blocksFiltered = blocksActive?.filter(
        (block) => block.id === currBlockID
      );
    } else if (currRanchID !== '' && currBlockID === '') {
      // If a ranch is selected but no block, show all blocks in ranch
      blocksFiltered = blocksActive?.filter(
        (block) => block.ranchID === currRanchID
      );
    } else if (
      currCustomerID !== '' &&
      currRanchID === '' &&
      currBlockID === ''
    ) {
      // If a customer is selected but no ranch or block, show all customer's blocks
      const ranchesFiltered = ranches?.filter(
        (ranch) => ranch.customerID === currCustomerID
      );
      const ranchIDs = ranchesFiltered?.map((ranch) => ranch.id);
      blocksFiltered = blocksActive?.filter((block) =>
        ranchIDs?.includes(block.ranchID)
      );
    }

    return blocksFiltered;
  }, [blocksActive, currBlockID, currRanchID, currCustomerID, ranches]);

  const {
    currentData: scansFilteredByBlock,
    isLoading,
    isFetching,
  } = useGetAllScansQuery(
    {
      // NOTE: Do NOT use this query when migrating over to the new UI for filtering
      customer__id__in: currCustomerID ? [currCustomerID] : undefined,
      ranch__id__in: currRanchID ? [currRanchID] : undefined,
      block__id__in: currBlockID ? [currBlockID] : undefined,
    },
    { skip: !currCustomerID }
  );

  // If a block is selected but no ranch, select the block's ranch.  This can happen if a user selects a block from the map when no ranch is selected.
  useEffect(() => {
    if (currBlockID !== '' && currRanchID === '') {
      const block = blocks?.find((block) => block.id === currBlockID);
      if (block) {
        dispatch(setCurrRanchByID(block.ranchID));
      }
    }
  }, [currBlockID, currRanchID, blocks, dispatch]);

  return {
    customersFiltered,
    ranchesFiltered,
    blocksFiltered,
    blocksActive,
    scansFilteredByBlock: scansFilteredByBlock,
    isLoadingScans: isLoading || isFetching,
  };
};
